import { ReactNode } from 'react';

import InputLabel from '@mui/material/InputLabel';
import classNames from 'classnames';

import { Box } from '../../../Box';
import { Typography, TypographyColor } from '../../../Typography';

import useStyles from './Label.styles';

/**
 * Свойства компонента {@link Label}
 */
interface LabelProps {
  /**
   * Текст метки.
   */
  label: string;

  /**
   * Всплывающая подсказка.
   *
   * @defaultValue `null`
   */
  tooltip?: ReactNode | null;

  /**
   * Цвет текста метки.
   *
   * @defaultValue `'text.light.secondary'`
   */
  color?: TypographyColor;

  /**
   * Иконка в правой части лейбла.
   *
   * @defaultValue `null`
   */
  icon?: ReactNode;

  /**
   * Вариант текста метки.
   *
   * @defaultValue `'body3'`
   */
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'body3'
    | 'caption'
    | 'button'
    | 'overline'
    | 'inherit';

  /**
   * Обязательное поле?
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Дополнительный CSS-класс.
   */
  className?: string;

  /**
   * ID связанного поля ввода.
   */
  inputId?: string;

  /**
   * Определяет положение лейбла над инпутом или в одну строку с инпутом.
   *
   * @defaultValue `false`
   */
  row?: boolean;
}

/**
 * Компонент для отображения метки поля ввода.
 *
 * @public
 * @param label - Текст метки.
 * @param tooltip - Всплывающая подсказка.
 * @param color - Цвет текста метки.
 * @param icon - Иконка в правой части лейбла.
 * @param variant - Вариант текста метки.
 * @param required - Обязательное поле?
 * @param className - Дополнительный CSS-класс.
 * @param inputId - ID связанного поля ввода.
 * @param row - Определяет положение лейбла над инпутом или в одну строку с инпутом.
 * @returns  Возвращает JSX-элемент представляющий компонент Label.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link LabelProps}
 */
export const Label = ({
  label,
  tooltip = null,
  color = 'text.light.secondary',
  icon,
  variant = 'body3',
  required = false,
  className,
  inputId,
  row = false,
}: LabelProps) => {
  const { classes } = useStyles({ row });

  return (
    <Box className={classes.labelBox}>
      <Box display="flex" alignItems="center" width="100%">
        <InputLabel
          className={classNames(classes.label, className, 'aqa_input_label')}
          htmlFor={inputId}
          shrink
        >
          <Typography
            className={required ? classes.required : ''}
            color={color}
            variant={variant}
          >
            {label}
          </Typography>
        </InputLabel>

        {tooltip && (
          <Box className={classes.tooltip} ml="4" maxHeight={20}>
            {tooltip}
          </Box>
        )}
      </Box>
      {icon && <Box>{icon}</Box>}
    </Box>
  );
};
